import { useState } from 'react';
import { jobError, jobLabel, timeAgo, useJobList, useRetryJob, type JobRow } from '../lib/jobs';
import { errorInfo } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Badge, EmptyState, ErrorMessage, Loading, PageHeader, type Tone } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'failed', label: 'Failed' },
] as const;
type Filter = (typeof FILTERS)[number]['key'];

const STATUS: Record<JobRow['status'], { label: string; tone: Tone }> = {
  pending: { label: 'Queued', tone: 'neutral' },
  active: { label: 'Running', tone: 'accent' },
  completed: { label: 'Done', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
};

export function JobsPage() {
  const jobs = useJobList();
  const [filter, setFilter] = useState<Filter>('all');
  const all = jobs.data?.jobs ?? [];
  const rows = all.filter((j) =>
    filter === 'all' ? true : filter === 'failed' ? j.status === 'failed' : j.status === 'pending' || j.status === 'active',
  );
  const failed = all.filter((j) => j.status === 'failed').length;

  return (
    <div>
      <PageHeader title="Activity" description="Uploads, transcriptions, AI tools and exports, as they run." />
      <div role="group" aria-label="Filter" className="mb-4 inline-flex rounded-md border border-border p-0.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded px-3 py-1 text-sm ${filter === f.key ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:text-text'}`}
          >
            {f.label}
            {f.key === 'failed' && failed > 0 ? ` (${failed})` : ''}
          </button>
        ))}
      </div>

      {jobs.isLoading && <Loading />}
      {jobs.isError && <ErrorMessage error={jobs.error} onRetry={() => jobs.refetch()} />}
      {jobs.data && rows.length === 0 && (
        <EmptyState
          title={filter === 'failed' ? 'Nothing has failed' : filter === 'running' ? 'Nothing is running' : 'No activity yet'}
          body={filter === 'all' ? 'Upload audio or run an AI tool and its progress shows up here.' : undefined}
        />
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {rows.map((j) => (
            <JobItem key={j.id} job={j} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JobItem({ job }: { job: JobRow }) {
  const retry = useRetryJob();
  const toast = useToast();
  const s = STATUS[job.status] ?? { label: job.status, tone: 'neutral' as const };
  const running = job.status === 'active' || job.status === 'pending';

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text">{jobLabel(job.jobType)}</span>
          <Badge tone={s.tone}>{s.label}</Badge>
        </div>
        {job.status === 'failed' && (
          <p className="mt-1 text-sm text-danger" title={job.errorMessage ?? undefined}>
            {jobError(job.errorMessage)}
          </p>
        )}
        {running && (
          <div
            role="progressbar"
            aria-label={`${jobLabel(job.jobType)} progress`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={job.progress}
            className="mt-2 h-1.5 max-w-xs overflow-hidden rounded-full bg-surface-2"
          >
            <div className="h-full bg-accent transition-all" style={{ width: `${Math.max(3, Math.min(100, job.progress))}%` }} />
          </div>
        )}
      </div>
      <span className="text-xs text-muted">{timeAgo(job.updatedAt ?? job.createdAt)}</span>
      {job.status === 'failed' && (
        <Button
          size="sm"
          variant="secondary"
          loading={retry.isPending}
          onClick={() =>
            retry.mutate(job.id, {
              onSuccess: () => toast.show(`Retrying ${jobLabel(job.jobType).toLowerCase()}`, 'success'),
              onError: (err) =>
                toast.show(errorInfo(err).code === 'not_retryable' ? 'This job can’t be retried.' : errorInfo(err).message, 'error'),
            })
          }
        >
          Retry
        </Button>
      )}
    </li>
  );
}
